/**
 * useMessagesSync Hook (Story 2.3.6.5, MSG-INBOX-TABS.6 AC-5)
 *
 * Global WebSocket subscription for messaging events, at app level so the header indicator
 * updates on ALL pages, not just MessagesPage.
 *
 * Responsibilities:
 * - Subscribe to `user.{guid}.messages` and to `merchant.{g}.messages` of every shop the user
 *   is a member of (from `unread-counts.merchants`), unsubscribing when a membership goes away
 * - On MESSAGE_RECEIVED: invalidate `unread-counts` and the lists of the event's context and
 *   type; show a notification + sound when the user is NOT viewing that conversation
 * - On CONVERSATION_STATUS_CHANGED (a shop blocked or unblocked the thread): re-read the thread's
 *   detail and the lists — also for the OPEN thread, whose composer / blocked panel follows the
 *   detail's status. The buyer is never sent the `internal` line that says so (TEAM-INBOX.8)
 * - Coordinate with ChatArea via activeConversationRef to avoid duplicates
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  useWebSocketOptional,
  useRealtimeEventOptional,
  EventType,
  RealtimeTopics,
  type MessageReceivedPayload,
  type ConversationStatusChangedPayload,
} from '@teletienda/ws-client/react';
import { useAuth } from '@teletienda/shared-auth-ui';
import { conversationKeys, teamGroupKey, useUnreadCounts } from '@/hooks/useConversations';
import { PERSONAL_CONTEXT, merchantContext, teamGroupShopOfEvent } from '@/lib/messenger/inboxContext';
import type { UnreadCounts } from '@/types/messenger';

/**
 * Module-level ref for the currently active (open) conversation ID.
 * ChatArea sets this on mount/unmount so the global handler can
 * suppress notifications for the conversation the user is viewing.
 */
export let activeConversationRef: string | null = null;

export function setActiveConversation(id: string | null): void {
  activeConversationRef = id;
}

/** Notification sound singleton */
let audioInstance: HTMLAudioElement | null = null;

function playNotificationSound(): void {
  try {
    if (!audioInstance) {
      audioInstance = new Audio('/sounds/message.wav');
    }
    audioInstance.currentTime = 0;
    audioInstance.play().catch(() => {
      /* browser may block autoplay */
    });
  } catch {
    /* ignore */
  }
}

/**
 * A staff member buying in their own shop takes part twice — as the buyer and as the shop —
 * and gets the same message on both topics. Both copies invalidate their own context; only the
 * first one rings.
 */
const recentlyNotified: string[] = [];
const RECENTLY_NOTIFIED_LIMIT = 50;

function firstDelivery(messageGuid: string): boolean {
  if (recentlyNotified.includes(messageGuid)) return false;
  recentlyNotified.push(messageGuid);
  if (recentlyNotified.length > RECENTLY_NOTIFIED_LIMIT) recentlyNotified.shift();
  return true;
}

/** Shops the user is a member of, as one stable string — the subscription effect's dependency */
function membershipKey(counts: UnreadCounts): string {
  return counts.merchants
    .map((m) => m.merchantGuid)
    .sort()
    .join(',');
}

/**
 * The lists an event touches. With `recipient`: the lists of that context — for «Personal»
 * narrowed to the event's type (its tab), a shop has no tabs. Without it (a publisher older
 * than MSG-INBOX-TABS.5): every list of every context.
 *
 * ⚠ The shop's team room is the exception (TEAM-INBOX.9 AC-11): its recipient is a PERSON, so by
 * `recipient` it would land in «Personal» (a `{ context: 'personal', type: 'group' }` list that
 * nothing reads). It belongs to the SHOP in `contextRef`, and the only thing it refreshes is that
 * shop's pinned row.
 */
export function listKeyForEvent(
  payload: Pick<MessageReceivedPayload, 'recipient' | 'conversationType' | 'contextRef'>
): QueryKey {
  const { recipient, conversationType } = payload;
  const roomShop = teamGroupShopOfEvent(payload);
  if (roomShop) return teamGroupKey(roomShop);
  if (!recipient) return conversationKeys.lists();
  if (recipient.type === 'merchant') {
    return conversationKeys.list({ context: merchantContext(recipient.guid) });
  }
  return conversationKeys.list({
    context: PERSONAL_CONTEXT,
    ...(conversationType ? { type: conversationType } : {}),
  });
}

/**
 * «Maria · Tomitos» — the person and the shop they answered for (`messages.reply_by`); the shop
 * alone when its policy hides the name; the legacy `senderName` without a structured sender.
 */
export function notificationTitleOf(payload: MessageReceivedPayload, t: TFunction): string {
  const person = payload.sender?.displayName || null;
  const shop = payload.sender?.onBehalfOf?.displayName || null;
  if (person && shop) return t('messages.reply_by', { staff: person, shop });
  return shop ?? person ?? payload.senderName;
}

export function useMessagesSync(): void {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const { user, isAuthenticated } = useAuth();
  const { isConnected, subscribe, unsubscribe } = useWebSocketOptional();
  const { data: memberships = '' } = useUnreadCounts(membershipKey);

  // The user's own topic
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !user?.userGuid) return;

    const topic = RealtimeTopics.userMessages(user.userGuid);
    subscribe([topic]);

    return () => {
      unsubscribe([topic]);
    };
  }, [isConnected, isAuthenticated, user?.userGuid, subscribe, unsubscribe]);

  // One topic per shop — events for a shop go to the shop's topic, not the person's
  // (ADR-002 D6). Re-run on a membership change: the cleanup drops the shops that went away.
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !memberships) return;

    const topics = memberships.split(',').map((guid) => RealtimeTopics.merchantMessages(guid));
    subscribe(topics);

    return () => {
      unsubscribe(topics);
    };
  }, [isConnected, isAuthenticated, memberships, subscribe, unsubscribe]);

  // Handle MESSAGE_RECEIVED events globally
  useRealtimeEventOptional<MessageReceivedPayload>(
    EventType.MESSAGE_RECEIVED,
    useCallback(
      (payload) => {
        // Skip own messages — but not a system line the person's own action wrote (an
        // assignment's `sender_guid` is its author): their other tabs and devices need it
        // (TEAM-INBOX.3 R-4). It makes no toast or sound, see below.
        if (payload.senderGuid === user?.userGuid && !payload.action) return;

        const isActiveConversation =
          activeConversationRef === payload.conversationGuid;

        // The team room's pinned row shows its last line: refreshed also while the room is open
        // (the open thread is ChatArea's, the row beside it is not)
        const roomShop = teamGroupShopOfEvent(payload);
        if (roomShop && isActiveConversation) {
          queryClient.invalidateQueries({ queryKey: teamGroupKey(roomShop) });
        }

        // Only invalidate for non-active conversations
        // ChatArea owns invalidation for the active conversation (messages + markAsRead)
        if (!isActiveConversation) {
          // Badges come only from the server — no client-side increment
          queryClient.invalidateQueries({ queryKey: conversationKeys.unreadCounts() });
          queryClient.invalidateQueries({ queryKey: listKeyForEvent(payload) });
          // Invalidate messages cache so opening this conversation later triggers a refetch
          // (useMessages has staleTime: Infinity — without this, cached data would be stale)
          queryClient.invalidateQueries({
            queryKey: conversationKeys.messages(payload.conversationGuid),
          });
          // Invalidate detail cache so ChatArea's useEffect sees fresh hasUnreadMessages: true
          // and triggers markAsRead (detail has staleTime: 60s — without this, stale cache
          // with hasUnreadMessages: false would prevent the markAsRead call)
          queryClient.invalidateQueries({
            queryKey: conversationKeys.detail(payload.conversationGuid),
          });
        }

        if (!firstDelivery(payload.messageGuid)) return;

        // A colleague's team note (TEAM-INBOX.4 AC-10): the thread and the list are refreshed
        // above, and that is all — this cabinet only shows notes, it does not announce them.
        // (The mention toast is Merchant Admin's.)
        if (payload.visibility === 'internal') return;

        // A system line about the thread (an assignment, TEAM-INBOX.3) reaches the shop's topic
        // like a message, but it is not one to be told about: refreshed above, no toast, no sound.
        if (payload.action) return;

        // The shop's own greeting / away message, as the SHOP's staff hear it (TEAM-INBOX.7): the
        // shop said something, nobody wrote to the team — refreshed above, no toast, no sound. The
        // buyer's copy (`recipient.type === 'user'`) is a message like any other and is announced.
        if (payload.autoReply && payload.recipient?.type === 'merchant') return;

        // Show notification only when NOT on the active conversation
        if (!isActiveConversation) {
          notifications.show({
            // «Valentina · Team» in the room; the usual «Maria · Tomitos» elsewhere
            title: roomShop
              ? t('messages.teamGroup.toastTitle', { name: payload.senderName })
              : notificationTitleOf(payload, t),
            message: payload.preview,
            color: 'blue',
            autoClose: 5000,
          });
        }

        // Play sound when tab hidden or not viewing that conversation
        if (document.hidden || !isActiveConversation) {
          playNotificationSound();
        }
      },
      [user?.userGuid, queryClient, t]
    ),
    [user?.userGuid, t]
  );
  // A shop blocked or unblocked a thread (TEAM-INBOX.8). Unlike MESSAGE_RECEIVED this is NOT left
  // to ChatArea for the open thread: ChatArea draws its composer or its «the shop does not accept
  // messages» panel from the detail's status, and nothing else tells it the status moved.
  useRealtimeEventOptional<ConversationStatusChangedPayload>(
    EventType.CONVERSATION_STATUS_CHANGED,
    useCallback(
      (payload) => {
        queryClient.invalidateQueries({
          queryKey: conversationKeys.detail(payload.conversationGuid),
        });
        queryClient.invalidateQueries({ queryKey: listKeyForEvent(payload) });
        queryClient.invalidateQueries({ queryKey: conversationKeys.unreadCounts() });
      },
      [queryClient]
    ),
    []
  );
}
